import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import { z } from 'zod'
import { apiFetch } from '@shared/api/http'
import { toQueryString } from '@shared/api/query'
import {
  RecuperaCartsSchema,
  RecuperaMetricsSchema,
  RecuperaProductsSchema,
  RecuperaRecoveryProductsSchema,
  RecuperaStatusSchema,
} from './recupera-ai.contract'
import type {
  RecuperaCartSort,
  RecuperaCartStatus,
  RecuperaCarts,
  RecuperaMetrics,
  RecuperaProducts,
  RecuperaRecoveryProducts,
  RecuperaStatus,
} from './recupera-ai.contract'

export interface RecuperaCartsFilters {
  status?: RecuperaCartStatus
  sort?: RecuperaCartSort
  search?: string
  product?: string
  dateFrom?: string
  dateTo?: string
}

export interface RecuperaMetricsRange {
  from: string
  to: string
}

export const recuperaAiKeys = {
  status: () => ['recupera-ai', 'status'] as const,
  carts: (filters: RecuperaCartsFilters) => ['recupera-ai', 'carts', filters] as const,
  metrics: (range: RecuperaMetricsRange) => ['recupera-ai', 'metrics', range] as const,
  products: () => ['recupera-ai', 'products'] as const,
  recoveryProducts: () => ['recupera-ai', 'recovery-products'] as const,
  productActive: (productId: string) => ['recupera-ai', 'product-active', productId] as const,
}

export function recuperaAiStatusQuery() {
  return queryOptions({
    queryKey: recuperaAiKeys.status(),
    queryFn: async (): Promise<RecuperaStatus> =>
      RecuperaStatusSchema.parse(await apiFetch<unknown>('/recupera/status')),
  })
}

export function recuperaAiProductActiveQuery(productId: string) {
  return queryOptions({
    queryKey: recuperaAiKeys.productActive(productId),
    queryFn: async (): Promise<{ active: boolean }> => {
      const parsed = z
        .object({ active: z.boolean() })
        .parse(await apiFetch<unknown>(`/recupera/products/${productId}/active`))
      return parsed
    },
  })
}

export function recuperaAiCartsInfiniteQuery(filters: RecuperaCartsFilters) {
  return infiniteQueryOptions({
    queryKey: recuperaAiKeys.carts(filters),
    queryFn: async ({ pageParam }): Promise<RecuperaCarts> => {
      const query = toQueryString({
        status: filters.status,
        sort: filters.sort,
        search: filters.search,
        product: filters.product,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        cursor: pageParam ?? undefined,
      })
      return RecuperaCartsSchema.parse(await apiFetch<unknown>(`/recupera/carts${query}`))
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: RecuperaCarts) => lastPage.next_cursor ?? undefined,
  })
}

export function recuperaAiProductsQuery() {
  return queryOptions({
    queryKey: recuperaAiKeys.products(),
    queryFn: async (): Promise<RecuperaProducts> =>
      RecuperaProductsSchema.parse(await apiFetch<unknown>('/recupera/products')),
  })
}

export function recuperaAiRecoveryProductsQuery() {
  return queryOptions({
    queryKey: recuperaAiKeys.recoveryProducts(),
    queryFn: async (): Promise<RecuperaRecoveryProducts> =>
      RecuperaRecoveryProductsSchema.parse(await apiFetch<unknown>('/recupera/recovery-products')),
  })
}

export function recuperaAiMetricsQuery(range: RecuperaMetricsRange) {
  return queryOptions({
    queryKey: recuperaAiKeys.metrics(range),
    queryFn: async (): Promise<RecuperaMetrics> => {
      const query = toQueryString({ from: range.from, to: range.to })
      return RecuperaMetricsSchema.parse(await apiFetch<unknown>(`/recupera/metrics${query}`))
    },
  })
}
